import React, {useState, useEffect} from 'react';
import {ChevronRight, ChevronLeft, Check} from 'lucide-react';
import Tooltip from '../common/Tooltip';
import CustomSelect from '../common/CustomSelect';
import './HedgeWizard.css';

/**
 * Wizard component for creating/editing hedge configurations
 * Breaks down complex form into 4 manageable steps
 */
const HedgeWizard = ({
    formData,
    setFormData,
    activeFields,
    setActiveFields,
    connections,
    onSubmit,
    onCancel,
    editingConfig,
    handleInputChange,
    handleRadioChange,
    errors = {},
    t
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const [touchedFields, setTouchedFields] = useState(new Set());

    // Calculate which step to start on when editing
    useEffect(() => {
        if (editingConfig) {
            // Start on first step when editing
            setCurrentStep(1);
        }
    }, [editingConfig]);

    // Scroll to top when step changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentStep]);

    // Wizard steps configuration
    const steps = [
        {
            number: 1,
            title: t('hedge.wizard.step1.title') || 'Market Setup',
            description: t('hedge.wizard.step1.description') || 'Choose your trading markets and symbols',
            icon: '🎯'
        },
        {
            number: 2,
            title: t('hedge.wizard.step2.title') || 'Position Parameters',
            description: t('hedge.wizard.step2.description') || 'Configure opening conditions and size',
            icon: '⚖️'
        },
        {
            number: 3,
            title: t('hedge.wizard.step3.title') || 'Hedge Settings',
            description: t('hedge.wizard.step3.description') || 'Set buffer zone and hedge strategy',
            icon: '🛡️'
        },
        {
            number: 4,
            title: t('hedge.wizard.step4.title') || 'Exit Strategy',
            description: t('hedge.wizard.step4.description') || 'Define closing conditions and take profit',
            icon: '🎯'
        }
    ];

    // Mark field as touched for validation display
    const handleFieldBlur = (fieldName) => {
        setTouchedFields(prev => new Set([...prev, fieldName]));
    };

    // Get required fields for a specific step
    const getRequiredFields = (stepNumber) => {
        switch (stepNumber) {
            case 1:
                // base_conn_id is only required if base_mrk is not fixprice
                const requiredStep1 = ['conf_descr', 'base_symbol', 'hedge_conn_id', 'hedge_symbol'];
                if (formData.base_mrk !== 'fixprice') {
                    requiredStep1.push('base_conn_id');
                }
                return requiredStep1;
            case 2:
                // At least one size parameter is required (mult OR open_qty)
                // At least one leverage parameter is required (incr_lvrg OR fix_lvrg)
                return [];
            case 3:
                return ['buff_perc'];
            case 4:
                return [];
            default:
                return [];
        }
    };

    // Get fields for a specific step
    const getStepFields = (stepNumber) => {
        switch (stepNumber) {
            case 1:
                return ['conf_descr', 'base_mrk', 'base_conn_id', 'base_symbol', 'base_dir',
                        'hedge_mrk', 'hedge_conn_id', 'hedge_symbol', 'fees'];
            case 2:
                return ['mult', 'open_qty', 'incr_lvrg', 'fix_lvrg', 'open_perc', 'open_price', 'open_dd'];
            case 3:
                return ['buff_perc', 'safe_zone_mode', 'hedge_tp_perc', 'hedge_tp_move_base', 'open_mode'];
            case 4:
                // force_close_hedge and force_close_all removed - managed via action buttons
                return ['be_act_perc', 'be_perc', 'close_after_hours', 'min_opened_time',
                        'check_close_interval', 'only_one_be'];
            default:
                return [];
        }
    };

    // Validate current step
    const validateStep = (stepNumber) => {
        const requiredFields = getRequiredFields(stepNumber);

        // Check if all required fields are filled
        for (const field of requiredFields) {
            if (!formData[field] || formData[field].toString().trim() === '') {
                return false;
            }
        }

        // Special validation for Step 2 - at least one size and leverage param
        if (stepNumber === 2) {
            const hasSize = formData.mult || formData.open_qty;
            const hasLeverage = formData.incr_lvrg || formData.fix_lvrg;
            if (!hasSize || !hasLeverage) {
                return false;
            }
        }

        // Check for errors in current step fields
        const stepFields = getStepFields(stepNumber);
        for (const field of stepFields) {
            if (errors[field]) {
                return false;
            }
        }

        return true;
    };

    // Check if next button should be enabled
    const isNextEnabled = () => {
        return validateStep(currentStep);
    };

    // Navigation handlers
    const handleNext = () => {
        // Mark all fields in current step as touched
        const stepFields = getStepFields(currentStep);
        setTouchedFields(prev => new Set([...prev, ...stepFields]));

        // Validate before moving
        if (!validateStep(currentStep)) {
            return;
        }

        // Mark step as completed
        setCompletedSteps(prev => new Set([...prev, currentStep]));

        // Move to next step
        if (currentStep < 4) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Check if step is clickable
    const isStepClickable = (stepNumber) => {
        // Current step is always clickable
        if (stepNumber === currentStep) {
            return true;
        }
        // Can always go back to previous or completed steps
        if (stepNumber < currentStep || completedSteps.has(stepNumber)) {
            return true;
        }
        // For future steps, can't go forward unless previous steps are completed
        // User must click Next button to mark step as completed
        return false;
    };

    const handleStepClick = (stepNumber) => {
        if (!isStepClickable(stepNumber)) {
            return;
        }
        setCurrentStep(stepNumber);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Only submit if we're on the last step
        if (currentStep !== 4) {
            return;
        }

        // Mark all fields as touched for final validation
        const allFields = [1, 2, 3, 4].flatMap(getStepFields);
        setTouchedFields(new Set(allFields));

        // Call parent submit handler
        onSubmit(e);
    };

    // Handle final submit button click (Step 4)
    const handleFinalSubmit = () => {
        // Create synthetic event
        const syntheticEvent = {
            preventDefault: () => {},
            target: {}
        };

        // Mark all fields as touched for final validation
        const allFields = [1, 2, 3, 4].flatMap(getStepFields);
        setTouchedFields(new Set(allFields));

        // Call parent submit handler
        onSubmit(syntheticEvent);
    };

    // Render step content
    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return <Step1Content
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFieldBlur={handleFieldBlur}
                    connections={connections}
                    errors={errors}
                    touchedFields={touchedFields}
                    t={t}
                />;
            case 2:
                return <Step2Content
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleRadioChange={handleRadioChange}
                    handleFieldBlur={handleFieldBlur}
                    activeFields={activeFields}
                    errors={errors}
                    touchedFields={touchedFields}
                    t={t}
                />;
            case 3:
                return <Step3Content
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFieldBlur={handleFieldBlur}
                    errors={errors}
                    touchedFields={touchedFields}
                    t={t}
                />;
            case 4:
                return <Step4Content
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFieldBlur={handleFieldBlur}
                    errors={errors}
                    touchedFields={touchedFields}
                    t={t}
                />;
            default:
                return null;
        }
    };

    return (
        <div className="hedge-wizard">
            {/* Progress Bar */}
            <div className="wizard-progress">
                {steps.map((step, index) => (
                    <React.Fragment key={step.number}>
                        <div
                            className={`wizard-step ${currentStep === step.number ? 'active' : ''}
                                       ${completedSteps.has(step.number) ? 'completed' : ''}
                                       ${!isStepClickable(step.number) ? 'disabled' : ''}`}
                            onClick={() => handleStepClick(step.number)}
                        >
                            <div className="step-indicator">
                                {completedSteps.has(step.number) ? (
                                    <Check size={20} />
                                ) : (
                                    <span>{step.number}</span>
                                )}
                            </div>
                            <div className="step-info">
                                <div className="step-title">{step.title}</div>
                                <div className="step-description">{step.description}</div>
                            </div>
                        </div>
                        {index < steps.length - 1 && (
                            <div className={`step-connector ${currentStep > step.number ? 'completed' : ''}`} />
                        )}
                    </React.Fragment>
                ))}
            </div>

            {/* Step Content */}
            <form
                onSubmit={handleSubmit}
                onKeyDown={(e) => {
                    // Prevent Enter key from ever submitting form
                    // Only allow explicit button clicks to submit
                    if (e.key === 'Enter') {
                        e.preventDefault();
                    }
                }}
            >
                <div className="wizard-content">
                    {renderStepContent()}
                </div>

                {/* Navigation */}
                <div className="wizard-navigation">
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={onCancel}
                    >
                        {t('common.cancel') || 'Cancel'}
                    </button>

                    <div className="nav-buttons">
                        {currentStep > 1 && (
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={handleBack}
                            >
                                <ChevronLeft size={20} />
                                {t('common.back') || 'Back'}
                            </button>
                        )}

                        {currentStep < 4 ? (
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={handleNext}
                                disabled={!isNextEnabled()}
                            >
                                {t('common.next') || 'Next'}
                                <ChevronRight size={20} />
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn-success"
                                onClick={handleFinalSubmit}
                                disabled={!isNextEnabled()}
                            >
                                <Check size={20} />
                                {editingConfig ? t('hedge.saveChanges') : t('hedge.createConfig')}
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

// Step 1: Market Setup
const Step1Content = ({formData, handleInputChange, handleFieldBlur, connections, errors, touchedFields, t}) => {
    return (
        <div className="wizard-step-content">
            <div className="form-group">
                <label htmlFor="conf_descr">
                    {t('hedge.configName') || 'Configuration Name'} *
                    <Tooltip content={t('hedge.tooltips.configName')} />
                </label>
                <input
                    type="text"
                    id="conf_descr"
                    name="conf_descr"
                    value={formData.conf_descr}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('conf_descr')}
                    placeholder={t('hedge.configNamePlaceholder') || 'My Hedge Strategy'}
                    className={errors.conf_descr && touchedFields.has('conf_descr') ? 'error' : ''}
                />
                {errors.conf_descr && touchedFields.has('conf_descr') && (
                    <span className="field-error">{errors.conf_descr}</span>
                )}
                <small className="field-hint">
                    {t('hedge.hints.configName') || 'Choose a descriptive name to easily identify this configuration'}
                </small>
            </div>

            <div className="form-grid-2">
                <div className="form-group">
                    <label htmlFor="base_mrk">
                        {t('hedge.baseMarket') || 'Base Market'} *
                        <Tooltip content={t('hedge.tooltips.baseMarket')} />
                    </label>
                    <CustomSelect
                        id="base_mrk"
                        name="base_mrk"
                        value={formData.base_mrk}
                        onChange={handleInputChange}
                        onBlur={() => handleFieldBlur('base_mrk')}
                        options={[
                            { value: 'futures', label: t('hedge.marketFutures') || 'Futures' },
                            { value: 'spot', label: t('hedge.marketSpot') || 'Spot' },
                            { value: 'fixprice', label: t('hedge.marketFixprice') || 'Fixed Price' }
                        ]}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="hedge_mrk">
                        {t('hedge.hedgeMarket') || 'Hedge Market'} *
                        <Tooltip content={t('hedge.tooltips.hedgeMarket')} />
                    </label>
                    <CustomSelect
                        id="hedge_mrk"
                        name="hedge_mrk"
                        value={formData.hedge_mrk}
                        onChange={handleInputChange}
                        onBlur={() => handleFieldBlur('hedge_mrk')}
                        options={[
                            { value: 'futures', label: t('hedge.marketFutures') || 'Futures' },
                            { value: 'spot', label: t('hedge.marketSpot') || 'Spot' }
                        ]}
                    />
                </div>
            </div>

            {formData.base_mrk !== 'fixprice' && (
                <div className="form-group">
                    <label htmlFor="base_conn_id">
                        {t('hedge.baseConnection') || 'Base Connection'} *
                        <Tooltip content={t('hedge.tooltips.baseConnection')} />
                    </label>
                    <CustomSelect
                        id="base_conn_id"
                        name="base_conn_id"
                        value={formData.base_conn_id}
                        onChange={handleInputChange}
                        onBlur={() => handleFieldBlur('base_conn_id')}
                        className={errors.base_conn_id && touchedFields.has('base_conn_id') ? 'error' : ''}
                        options={[
                            { value: '', label: t('hedge.selectConnection') || 'Select Connection' },
                            ...connections.filter(conn => conn.active === 1).map(conn => ({
                                value: conn.id,
                                label: `${conn.id} : ${conn.conn_descr} (${conn.exchange})`
                            }))
                        ]}
                    />
                    {errors.base_conn_id && touchedFields.has('base_conn_id') && (
                        <span className="field-error">{errors.base_conn_id}</span>
                    )}
                </div>
            )}

            <div className="form-grid-2">
                <div className="form-group">
                    <label htmlFor="base_symbol">
                        {t('hedge.baseSymbol') || 'Base Symbol'} *
                        <Tooltip content={t('hedge.tooltips.baseSymbol')} />
                    </label>
                    <input
                        type="text"
                        id="base_symbol"
                        name="base_symbol"
                        value={formData.base_symbol}
                        onChange={handleInputChange}
                        onBlur={() => handleFieldBlur('base_symbol')}
                        placeholder="BTCUSDT"
                        className={errors.base_symbol && touchedFields.has('base_symbol') ? 'error' : ''}
                    />
                    {errors.base_symbol && touchedFields.has('base_symbol') && (
                        <span className="field-error">{errors.base_symbol}</span>
                    )}
                </div>

                <div className="form-group">
                    <label htmlFor="hedge_symbol">
                        {t('hedge.hedgeSymbol') || 'Hedge Symbol'} *
                        <Tooltip content={t('hedge.tooltips.hedgeSymbol')} />
                    </label>
                    <input
                        type="text"
                        id="hedge_symbol"
                        name="hedge_symbol"
                        value={formData.hedge_symbol}
                        onChange={handleInputChange}
                        onBlur={() => handleFieldBlur('hedge_symbol')}
                        placeholder="BTCUSDT"
                        className={errors.hedge_symbol && touchedFields.has('hedge_symbol') ? 'error' : ''}
                    />
                    {errors.hedge_symbol && touchedFields.has('hedge_symbol') && (
                        <span className="field-error">{errors.hedge_symbol}</span>
                    )}
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="hedge_conn_id">
                    {t('hedge.hedgeConnection') || 'Hedge Connection'} *
                    <Tooltip content={t('hedge.tooltips.hedgeConnection')} />
                </label>
                <CustomSelect
                    id="hedge_conn_id"
                    name="hedge_conn_id"
                    value={formData.hedge_conn_id}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('hedge_conn_id')}
                    className={errors.hedge_conn_id && touchedFields.has('hedge_conn_id') ? 'error' : ''}
                    options={[
                        { value: '', label: t('hedge.selectConnection') || 'Select Connection' },
                        ...connections.filter(conn => conn.active === 1).map(conn => ({
                            value: conn.id,
                            label: `${conn.id} : ${conn.conn_descr} (${conn.exchange})`
                        }))
                    ]}
                />
                {errors.hedge_conn_id && touchedFields.has('hedge_conn_id') && (
                    <span className="field-error">{errors.hedge_conn_id}</span>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="fees">
                    {t('hedge.fees') || 'Trading Fees'} (%) *
                    <Tooltip content={t('hedge.tooltips.fees')} />
                </label>
                <input
                    type="number"
                    id="fees"
                    name="fees"
                    value={formData.fees}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('fees')}
                    step="0.01"
                    min="0"
                    max="1"
                    placeholder="0.1"
                />
                <small className="field-hint">
                    {t('hedge.hints.fees') || 'Typical exchange fees are 0.05-0.1%'}
                </small>
            </div>
        </div>
    );
};

// Step 2: Position Parameters
const Step2Content = ({formData, handleInputChange, handleRadioChange, handleFieldBlur, activeFields, errors, touchedFields, t}) => {
    return (
        <div className="wizard-step-content">
            <h3 className="section-title">{t('hedge.sizeParameters') || 'Position Size'}</h3>

            {formData.base_mrk === 'fixprice' ? (
                <div className="form-group">
                    <label htmlFor="open_qty">
                        {t('hedge.openQuantity') || 'Opening Quantity'}
                    </label>
                    <input
                        type="number"
                        id="open_qty"
                        name="open_qty"
                        value={formData.open_qty}
                        onChange={handleInputChange}
                        onBlur={() => handleFieldBlur('open_qty')}
                        step="any"
                    />
                </div>
            ) : (
                <div className="radio-group-enhanced">
                    <div className="radio-option-card">
                        <input
                            type="radio"
                            id="mult"
                            name="sizeGroup"
                            checked={activeFields.sizeGroup === 'mult'}
                            onChange={() => handleRadioChange('sizeGroup', 'mult')}
                        />
                        <label htmlFor="mult" className="radio-label">
                            <span className="radio-title">
                                {t('hedge.multiplier') || 'Multiplier'}
                                <Tooltip content={t('hedge.tooltips.multiplier')} />
                            </span>
                            <span className="radio-description">
                                {t('hedge.hints.multiplier') || 'Hedge size as ratio of base position'}
                            </span>
                        </label>
                        <input
                            type="number"
                            name="mult"
                            value={formData.mult}
                            onChange={handleInputChange}
                            onBlur={() => handleFieldBlur('mult')}
                            step="0.01"
                            min="0"
                            max="10"
                            disabled={activeFields.sizeGroup !== 'mult'}
                            placeholder="1.0"
                        />
                    </div>

                    <div className="radio-option-card">
                        <input
                            type="radio"
                            id="open_qty_radio"
                            name="sizeGroup"
                            checked={activeFields.sizeGroup === 'open_qty'}
                            onChange={() => handleRadioChange('sizeGroup', 'open_qty')}
                        />
                        <label htmlFor="open_qty_radio" className="radio-label">
                            <span className="radio-title">
                                {t('hedge.openQuantity') || 'Fixed Quantity'}
                                <Tooltip content={t('hedge.tooltips.openQuantity')} />
                            </span>
                            <span className="radio-description">
                                {t('hedge.hints.openQuantity') || 'Exact hedge position size'}
                            </span>
                        </label>
                        <input
                            type="number"
                            name="open_qty"
                            value={formData.open_qty}
                            onChange={handleInputChange}
                            onBlur={() => handleFieldBlur('open_qty')}
                            step="any"
                            disabled={activeFields.sizeGroup !== 'open_qty'}
                            placeholder="0.1"
                        />
                    </div>
                </div>
            )}

            {(formData.base_mrk !== 'fixprice' && formData.base_mrk !== 'spot') && (
                <>
                    <h3 className="section-title">{t('hedge.leverageParameters') || 'Leverage'}</h3>
                    <div className="radio-group-enhanced">
                        <div className="radio-option-card">
                            <input
                                type="radio"
                                id="incr_lvrg"
                                name="leverageGroup"
                                checked={activeFields.leverageGroup === 'incr_lvrg'}
                                onChange={() => handleRadioChange('leverageGroup', 'incr_lvrg')}
                            />
                            <label htmlFor="incr_lvrg" className="radio-label">
                                <span className="radio-title">
                                    {t('hedge.incrementalLeverage') || 'Incremental'}
                                    <Tooltip content={t('hedge.tooltips.incrementalLeverage')} />
                                </span>
                                <span className="radio-description">
                                    {t('hedge.hints.incrementalLeverage') || 'Leverage increases with position size'}
                                </span>
                            </label>
                            <input
                                type="number"
                                name="incr_lvrg"
                                value={formData.incr_lvrg}
                                onChange={handleInputChange}
                                onBlur={() => handleFieldBlur('incr_lvrg')}
                                disabled={activeFields.leverageGroup !== 'incr_lvrg'}
                                min="0"
                                placeholder="5"
                            />
                        </div>

                        <div className="radio-option-card">
                            <input
                                type="radio"
                                id="fix_lvrg"
                                name="leverageGroup"
                                checked={activeFields.leverageGroup === 'fix_lvrg'}
                                onChange={() => handleRadioChange('leverageGroup', 'fix_lvrg')}
                            />
                            <label htmlFor="fix_lvrg" className="radio-label">
                                <span className="radio-title">
                                    {t('hedge.fixedLeverage') || 'Fixed'}
                                    <Tooltip content={t('hedge.tooltips.fixedLeverage')} />
                                </span>
                                <span className="radio-description">
                                    {t('hedge.hints.fixedLeverage') || 'Constant leverage for all positions'}
                                </span>
                            </label>
                            <input
                                type="number"
                                name="fix_lvrg"
                                value={formData.fix_lvrg}
                                onChange={handleInputChange}
                                onBlur={() => handleFieldBlur('fix_lvrg')}
                                disabled={activeFields.leverageGroup !== 'fix_lvrg'}
                                min="1"
                                placeholder="10"
                            />
                        </div>
                    </div>
                </>
            )}

            {formData.base_mrk !== 'fixprice' && (
                <>
                    <h3 className="section-title">{t('hedge.openingConditions') || 'Opening Conditions'}</h3>
                    <div className="radio-group-enhanced">
                        <div className="radio-option-card">
                            <input
                                type="radio"
                                id="open_perc"
                                name="openGroup"
                                checked={activeFields.openGroup === 'open_perc'}
                                onChange={() => handleRadioChange('openGroup', 'open_perc')}
                            />
                            <label htmlFor="open_perc" className="radio-label">
                                <span className="radio-title">
                                    {t('hedge.openPercentage') || 'By Percentage'}
                                    <Tooltip content={t('hedge.tooltips.openPercentage')} />
                                </span>
                                <span className="radio-description">
                                    {t('hedge.hints.openPercentage') || 'Open when price moves X% from entry'}
                                </span>
                            </label>
                            <input
                                type="number"
                                name="open_perc"
                                value={formData.open_perc}
                                onChange={handleInputChange}
                                onBlur={() => handleFieldBlur('open_perc')}
                                step="any"
                                disabled={activeFields.openGroup !== 'open_perc'}
                                placeholder="1.0"
                            />
                        </div>

                        <div className="radio-option-card">
                            <input
                                type="radio"
                                id="open_price"
                                name="openGroup"
                                checked={activeFields.openGroup === 'open_price'}
                                onChange={() => handleRadioChange('openGroup', 'open_price')}
                            />
                            <label htmlFor="open_price" className="radio-label">
                                <span className="radio-title">
                                    {t('hedge.openPrice') || 'By Price'}
                                    <Tooltip content={t('hedge.tooltips.openPrice')} />
                                </span>
                                <span className="radio-description">
                                    {t('hedge.hints.openPrice') || 'Open at specific price level'}
                                </span>
                            </label>
                            <input
                                type="number"
                                name="open_price"
                                value={formData.open_price}
                                onChange={handleInputChange}
                                onBlur={() => handleFieldBlur('open_price')}
                                step="any"
                                disabled={activeFields.openGroup !== 'open_price'}
                                placeholder="50000"
                            />
                        </div>

                        <div className="radio-option-card">
                            <input
                                type="radio"
                                id="open_dd"
                                name="openGroup"
                                checked={activeFields.openGroup === 'open_dd'}
                                onChange={() => handleRadioChange('openGroup', 'open_dd')}
                            />
                            <label htmlFor="open_dd" className="radio-label">
                                <span className="radio-title">
                                    {t('hedge.openDrawdown') || 'By Drawdown'}
                                    <Tooltip content={t('hedge.tooltips.openDrawdown')} />
                                </span>
                                <span className="radio-description">
                                    {t('hedge.hints.openDrawdown') || 'Open when loss reaches X amount'}
                                </span>
                            </label>
                            <input
                                type="number"
                                name="open_dd"
                                value={formData.open_dd}
                                onChange={handleInputChange}
                                onBlur={() => handleFieldBlur('open_dd')}
                                step="any"
                                min="0"
                                disabled={activeFields.openGroup !== 'open_dd'}
                                placeholder="100"
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// Step 3: Hedge Settings
const Step3Content = ({formData, handleInputChange, handleFieldBlur, errors, touchedFields, t}) => {
    return (
        <div className="wizard-step-content">
            <div className="form-group">
                <label htmlFor="buff_perc">
                    {t('hedge.bufferPercentage') || 'Buffer Zone'} (%) *
                    <Tooltip content={t('hedge.tooltips.bufferPercentage')} />
                </label>
                <input
                    type="number"
                    id="buff_perc"
                    name="buff_perc"
                    value={formData.buff_perc}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('buff_perc')}
                    step="any"
                    min="0"
                    placeholder="0.5"
                    className={errors.buff_perc && touchedFields.has('buff_perc') ? 'error' : ''}
                />
                <small className="field-hint">
                    {t('hedge.hints.bufferPercentage') || 'Safety zone before hedge activates'}
                </small>
                {errors.buff_perc && touchedFields.has('buff_perc') && (
                    <span className="field-error">{errors.buff_perc}</span>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="safe_zone_mode">
                    {t('hedge.bufferZoneMode') || 'Buffer Zone Behavior'}
                    <Tooltip content={t('hedge.tooltips.bufferZoneMode')} />
                </label>
                <CustomSelect
                    id="safe_zone_mode"
                    name="safe_zone_mode"
                    value={formData.safe_zone_mode}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('safe_zone_mode')}
                    options={[
                        { value: 'follow', label: t('hedge.safeZoneModeFollow') || 'Follow Price' },
                        { value: 'expand', label: t('hedge.safeZoneModeExpand') || 'Expand Zone' }
                    ]}
                />
                <small className="field-hint">
                    {formData.safe_zone_mode === 'follow'
                        ? t('hedge.hints.followMode') || 'Buffer zone moves with price'
                        : t('hedge.hints.expandMode') || 'Buffer zone expands over time'}
                </small>
            </div>

            {formData.base_mrk !== 'fixprice' && (
                <>
                    <div className="form-group">
                        <label htmlFor="hedge_tp_perc">
                            {t('hedge.hedgeTakeProfit') || 'Hedge Take Profit'} (%)
                            <Tooltip content={t('hedge.tooltips.hedgeTakeProfit')} />
                        </label>
                        <input
                            type="number"
                            id="hedge_tp_perc"
                            name="hedge_tp_perc"
                            value={formData.hedge_tp_perc}
                            onChange={handleInputChange}
                            onBlur={() => handleFieldBlur('hedge_tp_perc')}
                            step="any"
                            min="0"
                            placeholder="1.0"
                        />
                        <small className="field-hint">
                            {t('hedge.hints.hedgeTakeProfit') || 'Close hedge when profit reaches this percentage'}
                        </small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="hedge_tp_move_base">
                            {t('hedge.hedgeTpMoveBase') || 'Move Base on Hedge TP'}
                            <Tooltip content={t('hedge.tooltips.hedgeTpMoveBase')} />
                        </label>
                        <CustomSelect
                            id="hedge_tp_move_base"
                            name="hedge_tp_move_base"
                            value={formData.hedge_tp_move_base}
                            onChange={handleInputChange}
                            onBlur={() => handleFieldBlur('hedge_tp_move_base')}
                            options={[
                                { value: '0', label: t('common.no') || 'No' },
                                { value: '1', label: t('common.yes') || 'Yes' }
                            ]}
                        />
                        <small className="field-hint">
                            {t('hedge.hints.hedgeTpMoveBase') || 'Adjust base position when hedge takes profit'}
                        </small>
                    </div>
                </>
            )}

            <div className="form-group">
                <label htmlFor="open_mode">
                    {t('hedge.openMode') || 'Order Type'}
                    <Tooltip content={t('hedge.tooltips.openMode')} />
                </label>
                <CustomSelect
                    id="open_mode"
                    name="open_mode"
                    value={formData.open_mode}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('open_mode')}
                    options={[
                        { value: 'market', label: t('hedge.openModeMarket') || 'Market Order' },
                        { value: 'limit', label: t('hedge.openModeLimit') || 'Limit Order' }
                    ]}
                />
                <small className="field-hint">
                    {formData.open_mode === 'market'
                        ? t('hedge.hints.marketOrder') || 'Execute immediately at market price'
                        : t('hedge.hints.limitOrder') || 'Place order at specific price'}
                </small>
            </div>
        </div>
    );
};

// Step 4: Exit Strategy
const Step4Content = ({formData, handleInputChange, handleFieldBlur, errors, touchedFields, t}) => {
    return (
        <div className="wizard-step-content">
            <h3 className="section-title">{t('hedge.breakEvenSettings') || 'Break-Even Protection'}</h3>

            <div className="form-group">
                <label htmlFor="be_act_perc">
                    {t('hedge.breakEvenActivationPercentage') || 'Activation Threshold'} (%) *
                    <Tooltip content={t('hedge.tooltips.breakEvenActivationPercentage')} />
                </label>
                <input
                    type="number"
                    id="be_act_perc"
                    name="be_act_perc"
                    value={formData.be_act_perc}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('be_act_perc')}
                    step="any"
                    min="0"
                    placeholder="1.0"
                    className={errors.be_act_perc && touchedFields.has('be_act_perc') ? 'error' : ''}
                />
                <small className="field-hint">
                    {t('hedge.hints.beActivation') || 'Profit level to activate break-even'}
                </small>
                {errors.be_act_perc && touchedFields.has('be_act_perc') && (
                    <span className="field-error">{errors.be_act_perc}</span>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="be_perc">
                    {t('hedge.breakEvenPercentage') || 'Break-Even Level'} (%) *
                    <Tooltip content={t('hedge.tooltips.breakEvenPercentage')} />
                </label>
                <input
                    type="number"
                    id="be_perc"
                    name="be_perc"
                    value={formData.be_perc}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('be_perc')}
                    step="any"
                    min="0"
                    placeholder="0.5"
                    className={errors.be_perc && touchedFields.has('be_perc') ? 'error' : ''}
                />
                <small className="field-hint">
                    {t('hedge.hints.bePerc') || 'Move stop loss to this profit level'}
                </small>
                {errors.be_perc && touchedFields.has('be_perc') && (
                    <span className="field-error">{errors.be_perc}</span>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="only_one_be">
                    {t('hedge.onlyOneBreakEven') || 'Single Break-Even Only'}
                    <Tooltip content={t('hedge.tooltips.onlyOneBreakEven')} />
                </label>
                <CustomSelect
                    id="only_one_be"
                    name="only_one_be"
                    value={formData.only_one_be}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('only_one_be')}
                    options={[
                        { value: '0', label: t('hedge.onlyOneBreakEvenNo') || 'Allow Multiple' },
                        { value: '1', label: t('hedge.onlyOneBreakEvenYes') || 'Only Once' }
                    ]}
                />
                <small className="field-hint">
                    {t('hedge.hints.onlyOneBe') || 'Whether to activate break-even only once'}
                </small>
            </div>

            <h3 className="section-title">{t('hedge.timeBasedExit') || 'Time-Based Rules'}</h3>

            {formData.base_mrk !== 'fixprice' && (
                <div className="form-group">
                    <label htmlFor="close_after_hours">
                        {t('hedge.closeAfterHours') || 'Auto-Close After'} (hours)
                        <Tooltip content={t('hedge.tooltips.closeAfterHours')} />
                    </label>
                    <input
                        type="number"
                        id="close_after_hours"
                        name="close_after_hours"
                        value={formData.close_after_hours}
                        onChange={handleInputChange}
                        onBlur={() => handleFieldBlur('close_after_hours')}
                        min="0"
                        placeholder="24"
                    />
                    <small className="field-hint">
                        {t('hedge.hints.closeAfterHours') || 'Close position after this many hours (0 = disabled)'}
                    </small>
                </div>
            )}

            <div className="form-group">
                <label htmlFor="min_opened_time">
                    {t('hedge.minOpenedTime') || 'Minimum Hold Time'} (seconds)
                    <Tooltip content={t('hedge.tooltips.minOpenedTime')} />
                </label>
                <input
                    type="number"
                    id="min_opened_time"
                    name="min_opened_time"
                    value={formData.min_opened_time}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('min_opened_time')}
                    min="0"
                    placeholder="60"
                />
                <small className="field-hint">
                    {t('hedge.hints.minOpenedTime') || 'Minimum time to hold position before allowing close'}
                </small>
            </div>

            <div className="form-group">
                <label htmlFor="check_close_interval">
                    {t('hedge.checkCloseInterval') || 'Check Close Interval'} (seconds)
                    <Tooltip content={t('hedge.tooltips.checkCloseInterval')} />
                </label>
                <input
                    type="number"
                    id="check_close_interval"
                    name="check_close_interval"
                    value={formData.check_close_interval}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('check_close_interval')}
                    min="0"
                    placeholder="0"
                    className={errors.check_close_interval && touchedFields.has('check_close_interval') ? 'error' : ''}
                />
                <small className="field-hint">
                    {t('hedge.hints.checkCloseInterval') || 'How often to check closing conditions (0 = every tick)'}
                </small>
                {errors.check_close_interval && touchedFields.has('check_close_interval') && (
                    <span className="field-error">{errors.check_close_interval}</span>
                )}
            </div>

            {/* Force Close fields are hidden - managed via action buttons in config list */}
        </div>
    );
};

export default HedgeWizard;
